import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// All translations
const translations = {
  en: {
    // Header
    appTitle: 'WELL-BEING SELF-ASSESSMENT',
    subtitle: 'Strengths Performance Centre',
    signIn: 'Sign In',
    signOut: 'Sign Out',
    userDetails: 'User Details',
    
    // Landing page
    tagline: 'Your Well-being Matters',
    heroTitle1: 'Feel Overwhelmed?',
    heroTitle2: 'Find Your Balance.',
    heroDescription: 'Most people struggle to identify what\'s affecting their well-being. This self-assessment helps you understand your current state across 6 key life elements – so you can take meaningful action.',
    beginAssessment: 'Begin Your Self-Assessment',
    assessmentInfo: 'Takes 5-7 minutes • Free PDF report • No credit card required',
    elementsTitle: '6 Elements of Well-being',
    elementsSubtitle: 'Gain awareness across all areas of your life',
    elementsExplainedTitle: 'The 6 Elements Explained',
    elementsExplainedSubtitle: 'Understanding each area helps you identify where to focus your energy',
    
    // Section names
    workCareer: 'Work & Career',
    lovedOnes: 'Loved Ones',
    emotionalMental: 'Emotional & Mental',
    interactionsOthers: 'Interactions With Others',
    physical: 'Physical',
    financial: 'Financial',
    
    // Section descriptions (short)
    workCareerDescShort: 'Most adults will spend over 54,000 hours at work throughout their 30-year career. As a huge investment of our time, it is important that we actually enjoy what we do each and every day.',
    lovedOnesDescShort: 'As human beings, we all need to love and be loved, especially by those closest to us. It is important to have positive relationships with those in your immediate circle.',
    emotionalMentalDescShort: 'A person\'s emotional and mental well-being relates to their stress, anxiety, depression, and ability to manage their emotions and keep perspective.',
    interactionsOthersDescShort: 'Your interactions with society and sense of belonging. As human beings, we have an innate need to feel like we are part of something.',
    physicalDescShort: 'More than just exercise - it encompasses physical activity, sleep, food nutrition, energy, and the link between mental and physical health.',
    financialDescShort: 'Not about how much money you have, but rather about having enough to deal with the challenges and expectations you have in life.',
    
    // Section descriptions (full)
    workCareerDescFull: 'Most adults will spend over 54,000 hours at work throughout their 30-year career. As a huge investment of our time, it is important that we actually enjoy what we do each and every day, whether working a regular 9-to-5, as a stay-at-home parent, or taking care of family. A person needs to be mindful of the nature of their work, their environment, their relationship with their manager and peers, and the recognition they receive.',
    lovedOnesDescFull: 'As human beings, we all need to love and be loved, especially by those closest to us. Regardless of the definition you give to your loved ones, it is important to have positive relationships with those in your immediate circle: spouse/partner, children, parents, siblings. But beyond our loved ones, it is important to establish and maintain friendships that support us and encourage us in difficult moments, and celebrate with us in good moments.',
    emotionalMentalDescFull: 'A person\'s emotional and mental well-being relates to their stress, anxiety, depression, and ability to manage their emotions. But rather than seeing it as a negative state, it is vital to acknowledge when we are in a bad state and take the necessary actions to get back to a good state. We need to be able to identify our emotional triggers, our mood, our self-talk, and our ability to reframe and keep perspective.',
    interactionsOthersDescFull: 'Where loved ones are defined as the close personal relationships in your life, "interactions with others" refers to your interactions with society. As human beings, we have an innate need to belong. We need to feel like we are part of something, whether the community in which we live, a club or association, or through social media. Whether introverted or extroverted, we are all social beings.',
    physicalDescFull: 'Our physical well-being is more than just getting enough exercise. It encompasses all aspects of our health: physical activity, sleep, food nutrition, and energy. We also tend to acknowledge the intimate link between our mental and physical health, and the impact of stress on our physical well-being. Ultimately, physical well-being relates to our ability to have enough energy in reserve to deal with any challenge we face in life.',
    financialDescFull: 'There\'s an old saying that money can\'t buy happiness. That may be true, but money can help make life easier. Your financial well-being is not about how much money you have, but rather about having enough to deal with the challenges and expectations you have. Money is not the root of all evil, but how we value money, our financial health, expectations around money, and our attitude towards money says a lot about our well-being.',
    
    // Assessment page
    progress: 'Progress',
    nextQuestion: 'Next question',
    complete: 'Complete',
    stronglyDisagree: 'Strongly disagree',
    stronglyAgree: 'Strongly agree',
    answerRequired: 'Please answer this question before continuing',
    loadingAssessment: 'Loading assessment...',
    assessmentNotFound: 'Assessment not found',
    returnHome: 'Return Home',
    
    // Registration page
    almostThere: 'Almost There!',
    enterDetails: 'Enter your details to receive your personalized report',
    firstName: 'First Name',
    lastName: 'Last Name',
    emailAddress: 'Email Address',
    createAccountOptional: 'Create an account (optional)',
    createAccountDesc: 'Save your results and track progress over time',
    createPassword: 'Create Password',
    confirmPassword: 'Confirm Password',
    atLeast8Chars: 'At least 8 characters',
    reenterPassword: 'Re-enter password',
    getResults: 'Get My Results',
    processing: 'Processing...',
    
    // Results page
    assessmentComplete: 'Assessment Complete!',
    reportGenerated: 'Your personalized well-being report has been generated',
    wellbeingState: 'Your Well-being State',
    basedOnResponses: 'Based on your responses across 6 key life elements',
    understandingResults: 'Understanding Your Results:',
    understandingResultsText: 'This assessment provides a snapshot of your current well-being across six key life elements. Remember, there is no "perfect" score, and your well-being naturally fluctuates based on life circumstances. These results are meant to provide awareness and serve as a starting point for reflection and positive change.',
    actionToConsider: 'Action to consider:',
    barDescription: 'This bar represents your overall well-being as you perceived it at the time of assessment.',
    importantRemember: 'Important to Remember:',
    importantRememberText: 'This is the cumulative result of all your answers across the 6 elements of well-being. The best results occur by working on all elements simultaneously, not by having a singular focus. Every step towards understanding and improving your well-being is valuable.',
    wellbeingByElement: 'Your Well-being by Element:',
    clickToExpand: 'Click on each element to expand for more details',
    saveResultsTitle: 'Would you like to save your results for future comparison?',
    saveResultsDesc: 'Create an account to track your progress over time. You can retake the assessment later and compare your results to see how your well-being has evolved.',
    saveMyResults: 'Save My Results',
    downloadReport: 'Download Your Report',
    downloadReportDesc: 'Get your complete 3-page PDF report with detailed insights',
    downloadPDF: 'Download PDF',
    emailSent: 'Email Sent!',
    emailSentDesc: 'We\'ve sent a copy of your report to your email. Check your inbox in the next few minutes.',
    reportAvailable: 'Report available for 60 days',
    whatsNext: 'What\'s Next?',
    reviewReport: 'Review Your Full Report',
    reviewReportDesc: 'Download and read through your complete assessment with personalized recommendations',
    takeAction: 'Take Action',
    takeActionDesc: 'Focus on areas that need attention and consider reaching out to a coach or counselor',
    retakeIn3Months: 'Retake in 3 Months',
    retakeIn3MonthsDesc: 'Track your progress by retaking the assessment after implementing changes',
    goToDashboard: 'Go To Dashboard',
    needSupport: 'Need support? Contact us at',
    orCall: 'or call',
    
    // Dashboard
    welcome: 'Welcome',
    retakeAssessment: 'Retake Well-Being Self-Assessment',
    assessmentHistory: 'Your Self-Assessment History',
    noAssessments: 'No self-assessments completed yet',
    takeFirstAssessment: 'Take Your First Self-Assessment',
    
    // Important note
    beforeYouBegin: 'Before You Begin',
    importantNote: 'IMPORTANT REMINDER:',
    importantNoteText: 'This questionnaire is uniquely intended to act as a guideline. It is not a psychometric tool, nor is it scientifically-based and researched. It was developed using many sources of information and resources.',
    assessmentDesc: 'The Well-being Self-Assessment will help you understand your current state across 6 key elements of life. It takes approximately 5-7 minutes to complete.',
    iUnderstand: 'I Understand - Begin Assessment',
    
    // Common
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    loading: 'Loading...',
  },
  fr: {
    // Header
    appTitle: 'AUTO-ÉVALUATION DU BIEN-ÊTRE',
    subtitle: 'Centre des points forts',
    signIn: 'Se connecter',
    signOut: 'Se déconnecter',
    userDetails: 'Détails de l\'utilisateur',
    
    // Landing page
    tagline: 'Votre bien-être compte',
    heroTitle1: 'Vous vous sentez dépassé?',
    heroTitle2: 'Trouvez votre équilibre.',
    heroDescription: 'La plupart des gens ont du mal à identifier ce qui affecte leur bien-être. Cette auto-évaluation vous aide à comprendre votre état actuel à travers 6 éléments clés de la vie – pour que vous puissiez agir de manière significative.',
    beginAssessment: 'Commencer l\'auto-évaluation',
    assessmentInfo: 'Prend 5-7 minutes • Rapport PDF gratuit • Aucune carte de crédit requise',
    elementsTitle: '6 éléments du bien-être',
    elementsSubtitle: 'Prenez conscience de tous les aspects de votre vie',
    elementsExplainedTitle: 'Les 6 éléments expliqués',
    elementsExplainedSubtitle: 'Comprendre chaque domaine vous aide à identifier où concentrer votre énergie',
    
    // Section names
    workCareer: 'Travail & Carrière',
    lovedOnes: 'Être Cher',
    emotionalMental: 'Émotionnel & Mental',
    interactionsOthers: 'Interaction avec les autres',
    physical: 'Physique',
    financial: 'Financier',
    
    // Section descriptions (short)
    workCareerDescShort: 'La plupart des adultes passeront plus de 54 000 heures au travail au cours de leur carrière de 30 ans. Comme il s\'agit d\'un investissement considérable de notre temps, il est important que nous aimions ce que nous faisons chaque jour.',
    lovedOnesDescShort: 'En tant qu\'êtres humains, nous avons tous besoin d\'aimer et d\'être aimés, surtout par ceux qui nous sont les plus proches. Il est important d\'avoir des relations positives avec ceux de notre cercle immédiat.',
    emotionalMentalDescShort: 'Le bien-être émotionnel et mental d\'une personne est lié à son stress, son anxiété, sa dépression et sa capacité à gérer ses émotions et à garder une perspective.',
    interactionsOthersDescShort: 'Vos interactions avec la société et votre sentiment d\'appartenance. En tant qu\'êtres humains, nous avons un besoin inné de sentir que nous faisons partie de quelque chose.',
    physicalDescShort: 'Plus que l\'exercice - cela englobe l\'activité physique, le sommeil, la nutrition, l\'énergie et le lien entre la santé mentale et physique.',
    financialDescShort: 'Il ne s\'agit pas de combien d\'argent vous avez, mais plutôt d\'en avoir suffisamment pour faire face aux défis et aux attentes de votre vie.',
    
    // Section descriptions (full)
    workCareerDescFull: 'La plupart des adultes passeront plus de 54 000 heures au travail au cours de leur carrière de 30 ans. Comme il s\'agit d\'un investissement considérable de notre temps, il est important que nous aimions ce que nous faisons chaque jour, que nous travaillions de 9 à 5, en tant que parent au foyer, ou en prenant soin de sa famille. Une personne doit être consciente de la nature de son travail, son environnement, sa relation avec son superviseur et ses pairs, et la reconnaissance qu\'elle reçoit.',
    lovedOnesDescFull: 'Nos êtres chers peuvent inclure les relations, les enfants, la famille et les amis proches que nous considérons comme de la famille. Non seulement le désir d\'aimer et d\'être aimé est considéré comme un besoin humain fondamental, mais nos proches nous aident aussi à nous sentir soutenus et prêts à tout. La façon dont nous donnons et recevons de l\'amour, communiquons, fixons des attentes, passons du temps de qualité et nous célébrons les uns les autres sont autant de facteurs qui contribuent à notre bien-être avec nos proches.',
    emotionalMentalDescFull: 'Les émotions font partie de notre existence humaine naturelle. Être capable d\'identifier nos émotions, comprendre ce qu\'elles sont, ce qui les déclenche, et apprendre à les gérer améliore notre bien-être émotionnel. Il est important d\'être attentif à notre état d\'esprit actuel, de reconnaître nos humeurs, la façon dont elles nous affectent et affectent les autres, et d\'apprendre à les gérer.',
    interactionsOthersDescFull: 'Malgré la croyance populaire, les introvertis comme les extravertis ont besoin d\'interagir avec les autres pour s\'épanouir. Tous les humains sont des créatures sociales, mais nos types d\'interactions peuvent varier en fonction de nos personnalités, de nos intérêts et de nos modes de vie. Des interactions superficielles sociales en passant par les interactions intimes, elles ont toutes un impact sur notre bien-être global et peuvent être influencées par des facteurs tels que le nombre d\'interactions ou les forces de notre réseau.',
    physicalDescFull: 'L\'élément de bien-être qui nous est le plus familier et sur lequel nous avons probablement le plus de contrôle est notre bien-être physique. Nos choix de mode de vie et nos habitudes quotidiennes ont une influence majeure sur notre bien-être global. Ce que nous mangeons, notre niveau d\'activité et notre temps de sommeil peuvent tous avoir un impact positif ou négatif sur notre bien-être physique.',
    financialDescFull: 'S\'assurer que nos besoins fondamentaux sont satisfaits, prendre en charge nos habitudes de dépenses et de notre budget, se créer un fond de sécurité et planifier notre avenir financier sont tous des facteurs qui peuvent contribuer à influencer cet élément. Ne pas avoir de plan ou ne pas savoir comment répondre à ces besoins nuira à notre bien-être. Apprendre à vivre selon nos moyens financiers fait donc partie intégrante d\'un bien-être financier sain.',
    
    // Assessment page
    progress: 'Progrès',
    nextQuestion: 'Question suivante',
    complete: 'Terminer',
    stronglyDisagree: 'Fortement en désaccord',
    stronglyAgree: 'Entièrement d\'accord',
    answerRequired: 'Veuillez répondre à cette question avant de continuer',
    loadingAssessment: 'Chargement de l\'auto-évaluation...',
    assessmentNotFound: 'Auto-évaluation non trouvée',
    returnHome: 'Retour à l\'accueil',
    
    // Registration page
    almostThere: 'Presque terminé!',
    enterDetails: 'Entrez vos coordonnées pour recevoir votre rapport personnalisé',
    firstName: 'Prénom',
    lastName: 'Nom',
    emailAddress: 'Adresse courriel',
    createAccountOptional: 'Créer un compte (optionnel)',
    createAccountDesc: 'Enregistrez vos résultats et suivez vos progrès au fil du temps',
    createPassword: 'Créer un mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    atLeast8Chars: 'Au moins 8 caractères',
    reenterPassword: 'Ressaisir le mot de passe',
    getResults: 'Obtenir mes résultats',
    processing: 'Traitement en cours...',
    
    // Results page
    assessmentComplete: 'Auto-évaluation terminée!',
    reportGenerated: 'Votre rapport de bien-être personnalisé a été généré',
    wellbeingState: 'Votre état de bien-être',
    basedOnResponses: 'Basé sur vos réponses à travers 6 éléments clés de la vie',
    understandingResults: 'Comprendre vos résultats:',
    understandingResultsText: 'Cette évaluation fournit un aperçu de votre bien-être actuel à travers six éléments clés de la vie. N\'oubliez pas qu\'il n\'y a pas de score "parfait" et que votre bien-être fluctue naturellement en fonction des circonstances de la vie. Ces résultats sont destinés à sensibiliser et à servir de point de départ pour la réflexion et le changement positif.',
    actionToConsider: 'Action à considérer:',
    barDescription: 'Cette barre représente votre bien-être global tel que vous l\'avez perçu au moment de l\'évaluation.',
    importantRemember: 'Important à retenir:',
    importantRememberText: 'Il s\'agit du résultat cumulé de toutes vos réponses aux 6 éléments du bien-être. Les meilleurs résultats sont obtenus en travaillant sur tous les éléments en même temps, et non en se concentrant sur un seul. Chaque étape vers la compréhension et l\'amélioration de votre bien-être est précieuse.',
    wellbeingByElement: 'Votre bien-être par élément:',
    clickToExpand: 'Cliquez sur chaque élément pour plus de détails',
    saveResultsTitle: 'Souhaitez-vous enregistrer vos résultats pour une comparaison future?',
    saveResultsDesc: 'Créez un compte pour suivre vos progrès au fil du temps. Vous pouvez reprendre l\'évaluation plus tard et comparer vos résultats pour voir comment votre bien-être a évolué.',
    saveMyResults: 'Enregistrer mes résultats',
    downloadReport: 'Télécharger votre rapport',
    downloadReportDesc: 'Obtenez votre rapport PDF complet de 3 pages avec des informations détaillées',
    downloadPDF: 'Télécharger PDF',
    emailSent: 'Courriel envoyé!',
    emailSentDesc: 'Nous avons envoyé une copie de votre rapport à votre courriel. Vérifiez votre boîte de réception dans les prochaines minutes.',
    reportAvailable: 'Rapport disponible pendant 60 jours',
    whatsNext: 'Quelle est la suite?',
    reviewReport: 'Examiner votre rapport complet',
    reviewReportDesc: 'Téléchargez et lisez votre auto-évaluation complète avec des recommandations personnalisées',
    takeAction: 'Passer à l\'action',
    takeActionDesc: 'Concentrez-vous sur les domaines qui nécessitent de l\'attention et envisagez de contacter un coach ou un conseiller',
    retakeIn3Months: 'Reprendre dans 3 mois',
    retakeIn3MonthsDesc: 'Suivez vos progrès en reprenant l\'auto-évaluation après avoir mis en œuvre des changements',
    goToDashboard: 'Aller au tableau de bord',
    needSupport: 'Besoin d\'aide? Contactez-nous à',
    orCall: 'ou appelez',
    
    // Dashboard
    welcome: 'Bienvenue',
    retakeAssessment: 'Reprendre l\'auto-évaluation du bien-être',
    assessmentHistory: 'Votre historique d\'auto-évaluation',
    noAssessments: 'Aucune auto-évaluation complétée',
    takeFirstAssessment: 'Faire votre première auto-évaluation',
    
    // Important note
    beforeYouBegin: 'Avant de commencer',
    importantNote: 'RAPPEL IMPORTANT:',
    importantNoteText: 'Cette auto-évaluation est uniquement destinée à servir de guide. Il ne s\'agit pas d\'un outil psychométrique, ni d\'un outil basé sur la science et la recherche. Elle a été élaborée en utilisant de nombreuses sources d\'information et de ressources.',
    assessmentDesc: 'L\'auto-évaluation du bien-être vous aidera à comprendre votre état actuel à travers 6 éléments clés de la vie. Cela prend environ 5-7 minutes.',
    iUnderstand: 'Je comprends - Commencer l\'auto-évaluation',
    
    // Common
    cancel: 'Annuler',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    loading: 'Chargement...',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || translations['en'][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'fr' : 'en');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    translations: translations[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
